import { Stage, StageInfo } from './Stage/Stage';
import styles from './Stages.module.scss';
import { Text } from 'components';
import { Icon, Icons } from 'components/Icon/Icon';
import { selectCurrentPatientCase } from 'store/patients/selectors';
import { useSelector } from 'store/utils';
import { PatientFileType, TreatmentPlanTask, TreatmentPlanType } from 'types';

const stages: Record<
  string,
  {
    icon: Icons;
    steps: StageInfo[];
  }
> = {
  Submit: {
    icon: 'Diagnosis',
    steps: [
      {
        title: 'Submit patient case',
        description: 'New patient case is created and needs Studio submission',
        task: TreatmentPlanTask.TO_SUBMIT,
        alertText: 'Please make sure the complaint and composite view image are added to proceed',
        controls: {
          nextStepTask: TreatmentPlanTask.DIAGNOSIS_TO_DO,
          nextStepText: 'Submit',
          nextStepDoctorActions: false,
          complaintWithImageIsRequired: true,
        },
      },
    ],
  },
  'New patient': {
    icon: 'DiagnosisLarge',
    steps: [
      {
        title: 'Diagnosis and Treatment plan required',
        description:
          'Patient is required a Diagnosis and Treatment plan from the Doctor. Please check information of the patient case and provide Diagnosis and Treatment plan',
        task: TreatmentPlanTask.DIAGNOSIS_TO_DO,
        alertText: 'Please make sure the Diagnosis and Treatment plan is added to proceed',
        controls: {
          nextStepTask: TreatmentPlanTask.DIAGNOSIS_SUBMITTED,
          nextStepText: 'Confirm TX',
          nextStepDoctorActions: true,
          diagnosisIsRequired: true,
          treatmentPlanIsRequired: true,
        },
      },
      {
        title: 'Diagnosis and Treatment plan submitted',
        description: 'Studio should add the link to the draft of the Letter document for Doctor to check',
        task: TreatmentPlanTask.DIAGNOSIS_SUBMITTED,
        alertText: 'Please make sure the Letter link is added to proceed',
        controls: {
          nextStepTask: TreatmentPlanTask.LETTER_TO_DO,
          nextStepText: 'Submit letter',
          stepBackTask: TreatmentPlanTask.DIAGNOSIS_TO_DO,
          stepBackText: 'Edit TX',
          nextStepDoctorActions: false,
          stepBackDoctorActions: true,
          letterLinkRequired: true,
        },
      },
    ],
  },
  Letter: {
    icon: 'Letter',
    steps: [
      {
        title: 'Letter review required',
        description:
          'Studio has added the link to the draft of the Letter document for Doctor to check. Please review and corrected a draft of a Letter',
        task: TreatmentPlanTask.LETTER_TO_DO,
        alertText: 'Please make sure the Letter link is added to proceed',
        controls: {
          nextStepTask: TreatmentPlanTask.LETTER_SUBMITTED,
          nextStepText: 'Confirm letter',
          stepBackTask: TreatmentPlanTask.DIAGNOSIS_SUBMITTED,
          stepBackText: 'Edit Letter',
          nextStepDoctorActions: true,
          stepBackDoctorActions: false,
          letterLinkRequired: true,
        },
      },
      {
        title: 'Letter approved',
        description: 'Letter is approved by the Doctor and the Studio can send the letter to the patient',
        task: TreatmentPlanTask.LETTER_SUBMITTED,
        controls: {
          nextStepTask: TreatmentPlanTask.LETTER_SENT,
          nextStepText: 'Confirm sent',
          stepBackTask: TreatmentPlanTask.DIAGNOSIS_SUBMITTED,
          stepBackText: 'Edit letter',
          nextStepDoctorActions: false,
          stepBackDoctorActions: true,
          letterLinkRequired: true,
        },
      },
      {
        title: 'Letter sent',
        description: 'Letter has been sent to the patient and the patient can start the treatment',
        task: TreatmentPlanTask.LETTER_SENT,
        alertText: 'Please make sure the Treatment plan and Aligner Final Treatment plan is added to proceed',
        controls: {
          nextStepTask: TreatmentPlanTask.IN_TREATMENT,
          nextStepText: 'Start treatment',
          stepBackTask: TreatmentPlanTask.DIAGNOSIS_SUBMITTED,
          stepBackText: 'Edit letter',
          nextStepDoctorActions: false,
          stepBackDoctorActions: true,
          treatmentPlanIsRequired: true,
        },
      },
    ],
  },
  Treatment: {
    icon: 'inTreatment',
    steps: [
      {
        title: 'In treatment',
        description: 'The patient is being treated. Dental monitoring updates will be provided',
        task: TreatmentPlanTask.IN_TREATMENT,
        controls: {
          nextStepTask: TreatmentPlanTask.TREATMENT_PRE_FINISH,
          nextStepText: 'Pre-finish check',
          nextStepDoctorActions: false,
        },
      },
    ],
  },
  Finished: {
    icon: 'FinishedSmall',
    steps: [
      {
        title: 'Pre-finish check',
        description: 'Pre-finish check should be performed',
        task: TreatmentPlanTask.TREATMENT_PRE_FINISH,
        controls: {
          nextStepTask: TreatmentPlanTask.TREATMENT_POST_FINISH,
          nextStepText: 'Post-finish check',
          stepBackTask: TreatmentPlanTask.IN_TREATMENT,
          stepBackText: 'Back to treatment',
          nextStepDoctorActions: false,
          stepBackDoctorActions: false,
        },
      },
      {
        title: 'Post-finish check',
        description: 'Post-finish check should be performed',
        task: TreatmentPlanTask.TREATMENT_POST_FINISH,
        controls: {
          nextStepTask: TreatmentPlanTask.COMPLETED,
          nextStepText: 'Complete treatment',
          stepBackTask: TreatmentPlanTask.TREATMENT_PRE_FINISH,
          stepBackText: 'Pre-finish check',
          nextStepDoctorActions: false,
          stepBackDoctorActions: false,
        },
      },
      {
        title: 'Complete treatment',
        description: 'Treatment is completed',
        task: TreatmentPlanTask.COMPLETED,
        controls: {
          stepBackTask: TreatmentPlanTask.TREATMENT_POST_FINISH,
          stepBackText: 'Post-finish check',
          nextStepDoctorActions: false,
          stepBackDoctorActions: false,
        },
      },
    ],
  },
};

export const Stages = () => {
  const currentPatientCase = useSelector(selectCurrentPatientCase);

  if (!currentPatientCase) return null;

  const {
    task: currentTask,
    id,
    isTaskBlocked,
    assignedDoctorId,
    diagnosis,
    treatmentPlan,
    letterLinks,
    patientComplaint,
    files,
    treatmentPlanType,
  } = currentPatientCase;

  const hasComplaintWithImage =
    !!patientComplaint && !!files.find((file) => file.type === PatientFileType.COMPOSITE_VIEW);
  const hasCorrectTreatmentPlan =
    !!treatmentPlan &&
    (currentTask === TreatmentPlanTask.LETTER_SENT ? treatmentPlanType !== TreatmentPlanType.NONE : true);

  function renderStagesContent() {
    let beforeCurrentAndPassed = true;
    return Object.entries(stages).map(([groupName, { icon, steps }], index) => (
      <div key={groupName} className={styles.group}>
        {index !== 0 && (
          <div className={styles.groupHeader}>
            <Icon className={styles.icon} name={icon} />
            <Text className={styles.groupName} type="overline">
              {groupName}
            </Text>
          </div>
        )}
        <div className={styles.groupSteps}>
          {steps.map((step) => {
            const isCurrentTask = step.task === currentTask;
            const isPassed = beforeCurrentAndPassed && !isCurrentTask;

            if (isCurrentTask) {
              beforeCurrentAndPassed = false;
            }

            return (
              <Stage
                key={step.title}
                isCurrent={isCurrentTask}
                isPassed={isPassed}
                stageInfo={step}
                patientCaseId={id}
                assignedDoctorId={assignedDoctorId}
                isOutOfTreatment={isTaskBlocked}
                diagnosis={diagnosis}
                letterLinks={letterLinks}
                hasCorrectTreatmentPlan={hasCorrectTreatmentPlan}
                hasComplaintWithImage={hasComplaintWithImage}
              />
            );
          })}
        </div>
      </div>
    ));
  }
  return <div className={styles.container}>{renderStagesContent()}</div>;
};
